import type { ReactNode } from 'react';
import type { OrderSummary } from '../../data/orders.ts';
import { LayoutGridIcon, TableIcon } from '../../ui/icons.tsx';
import { RevealItem } from '../../ui/RevealItem.tsx';
import { OrderCard } from './OrderCard.tsx';
import { OrderTable } from './OrderTable.tsx';
import type { OrderView } from './useOrderView.ts';

interface OrderListProps {
  orders: OrderSummary[];
  view: OrderView;
  staff: boolean;
  hideCustomer?: boolean;
}

// Lista de órdenes (panel y ficha del cliente) en la vista elegida: tarjetas o tabla.
export function OrderList({ orders, view, staff, hideCustomer = false }: OrderListProps) {
  if (view === 'table') {
    return orders.length > 0 ? <OrderTable orders={orders} staff={staff} hideCustomer={hideCustomer} /> : null;
  }
  return (
    <ul className="order-list">
      {orders.map((order) => (
        <RevealItem key={order.id}>
          <OrderCard order={order} staff={staff} hideCustomer={hideCustomer} />
        </RevealItem>
      ))}
    </ul>
  );
}

const VIEWS: { id: OrderView; label: string; icon: ReactNode }[] = [
  { id: 'cards', label: 'Tarjetas', icon: <LayoutGridIcon /> },
  { id: 'table', label: 'Tabla', icon: <TableIcon /> },
];

// Selector de vista. En pantallas angostas solo se ven los íconos; el nombre sigue disponible para lectores de pantalla.
export function OrderViewToggle({ view, onChange }: { view: OrderView; onChange: (view: OrderView) => void }) {
  return (
    <div className="view-toggle" role="group" aria-label="Vista de las órdenes">
      {VIEWS.map((option) => (
        <button
          key={option.id}
          type="button"
          className="view-toggle-option"
          aria-pressed={view === option.id}
          onClick={() => onChange(option.id)}
        >
          {option.icon}
          <span className="view-toggle-label">{option.label}</span>
        </button>
      ))}
    </div>
  );
}
